import apiV2Service from '../api/api-v2.service';
import apiService from '../api/api.service';
import {ApiOptions, Payload} from '../api/apiTypes';
import {
  PasswordValidationRules,
  SignOutResponse,
  SSOUrlResponse,
  UserAuthBaseInfo,
  UserAuthInfo,
} from './auth.types';

const basePath = 'authentication';
const v2BasePath = 'Authentication';

async function signIn(email: string, password: string): Promise<UserAuthInfo> {
  const {data} = await apiV2Service.executePostRequest<UserAuthInfo>(
    `${v2BasePath}/SignIn`,
    {},
    {email, password},
    {includeClientId: false},
  );

  return data;
}

async function validatePassword(
  newPassword: string,
  email: string,
): Promise<PasswordValidationRules> {
  const {data} = await apiV2Service.executePostRequest<PasswordValidationRules>(
    `${v2BasePath}/ValidatePassword`,
    {},
    {newPassword, email},
    {includeClientId: false},
  );

  return data;
}

async function validatePasswordForNewUser({
  password,
  lastName,
  firstName,
  registrationToken,
}: {
  password: string;
  registrationToken: string;
  firstName: string;
  lastName: string;
}): Promise<PasswordValidationRules> {
  const options: ApiOptions = {
    skipAuth: true,
    includeClientId: false,
    headers: {Authorization: `Bearer ${registrationToken}`},
  };

  const {data} = await apiV2Service.executePostRequest<PasswordValidationRules>(
    `${v2BasePath}/ValidatePasswordForNewUser`,
    {},
    {password, lastName, firstName},
    options,
  );

  return data;
}

async function verifyMultiFactorCode(email: string, mfaCode: string): Promise<UserAuthInfo> {
  const {data} = await apiV2Service.executePostRequest<UserAuthInfo>(
    `${v2BasePath}/VerifyMultiFactorCode`,
    {},
    {email, mfaCode},
    {includeClientId: false},
  );

  return data;
}

function forgotPasswordSendEmail(email: string): Promise<Payload<void>> {
  return apiService.executePostRequest<void>(`${basePath}/ForgotPasswordSendEmail`, {email}, null, {
    skipAuth: true,
  });
}

async function logout() {
  const {
    data: {singleSignOutRedirectUrl},
  } = await apiV2Service.executePostRequest<SignOutResponse>(
    `${v2BasePath}/SignOut`,
    {},
    {},
    {includeClientId: false},
  );

  return singleSignOutRedirectUrl;
}

function contactUsEmail(
  name: string,
  email: string,
  subject: string,
  body: string,
  requestSource: string,
): Promise<Payload<void>> {
  const postObj = {
    name,
    email,
    subject,
    body,
    requestSource,
  };

  return apiService.executePostRequest(`${basePath}/ContactUsEmail`, null, postObj, {
    skipAuth: true,
  });
}

async function changePassword({
  oldPassword,
  newPassword,
  email,
}: {
  oldPassword: string;
  newPassword: string;
  email: string;
}): Promise<UserAuthBaseInfo> {
  const paramsObj = {
    oldPassword,
    newPassword,
    email,
  };

  const {
    data: {token, clientId},
  } = await apiV2Service.executePostRequest<UserAuthBaseInfo>(
    `${basePath}/ChangePassword`,
    {},
    paramsObj,
    {includeClientId: false},
  );

  return {token, clientId};
}

async function changePasswordByResetToken({
  password,
  passwordResetToken,
}: {
  password: string;
  passwordResetToken: string;
}): Promise<unknown> {
  const paramsObj = {
    password,
    passwordResetToken,
  };

  return apiV2Service.executePostRequest(`${basePath}/ChangePasswordByResetToken`, {}, paramsObj, {
    includeClientId: false,
  });
}

async function getSsoUrl(userEmail: string): Promise<string> {
  const {
    data: {ssoUrl},
  } = await apiV2Service.executeGetRequest<SSOUrlResponse>(
    `${basePath}/SignInMethod/${userEmail}`,
    null,
    {
      skipAuth: true,
      includeClientId: false,
    },
  );

  return ssoUrl;
}

const authApi = {
  signIn,
  forgotPasswordSendEmail,
  contactUsEmail,
  changePassword,
  validatePassword,
  verifyMultiFactorCode,
  logout,
  changePasswordByResetToken,
  validatePasswordForNewUser,
  getSsoUrl,
};

export default authApi;
